import React from "react";
import { Box, Typography, Switch, Radio, RadioGroup } from "@mui/material";
import { CustomAutoComplete } from "components/CustomComponents";
import { themes } from "services/constants";
import {
  type,
  SftPenaltyPolicyExample,
  SftPenaltyPolicySftExample,
  SftPenaltyPolicyCountExample,
  penaltyOptions,
  deductionOptions,
  boxStyle,
  CustomBox,
  optionsStyle,
  individualBoxStyle,
  infoMessageStyle,
  additionalInfoStyle,
  cardStyle,
  mainOptions,
} from "../ShiftCategoryOptions";

export const ApplyShiftPenaltyPolicy = ({
  sftPenaltyPolicy,
  watch,
  setValue,
  register,
  errors,
  resetAttendanceSftPenalty,
  resetSftPenalty,
  resetCountPenalty,
  hanldePenaltyOptions,
  hanldeDeductionOptions,
}) => {
  if (sftPenaltyPolicy) {
    return null;
  }

  return (
    <Box sx={mainOptions}>
      <Box sx={boxStyle(watch("is_apply_sft_penalty_policy"))}>
        <Typography fontWeight="medium">Apply shift penalty policy</Typography>

        <Switch
          checked={watch("is_apply_sft_penalty_policy")}
          onChange={(event) => {
            const checked = event.target.checked;
            setValue("is_apply_sft_penalty_policy", checked);
            if (!checked) {
              setValue("penalty_options", "hours");
              setValue("dedctions_options", "Attendance");
              resetAttendanceSftPenalty();
              resetSftPenalty();
              resetCountPenalty();
            }
          }}
        />
      </Box>
      {watch("is_apply_sft_penalty_policy") && (
        <>
          <Box sx={{ ml: 2, mb: 2 }}>
            <Typography fontWeight="medium">Penalty based on</Typography>
            <Typography variant="body2" color="text.secondary">
              Defines how penalties or adjustments are calculated.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={watch("penalty_options")}
              onChange={hanldePenaltyOptions}
              sx={optionsStyle}
            >
              {penaltyOptions.map((opt) => {
                const isActive = watch("penalty_options") === opt.id;
                return (
                  <Box key={opt.id} sx={cardStyle(isActive)}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Radio value={opt.id} />
                      <Typography
                        fontWeight="medium"
                        sx={{
                          color: isActive ? themes.stepActiveColor : "inherit",
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 4, maxWidth: 250, wordBreak: "break-word" }}
                    >
                      {opt.description}
                    </Typography>
                  </Box>
                );
              })}
            </RadioGroup>
          </Box>
          {watch("penalty_options") === "hours" && (
            <>
              <Box sx={{ ml: 2, mb: 2 }}>
                <Typography fontWeight="medium">
                  Deduction calculation based on
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose between payment factor (linked to attendance) or fixed
                  amount.
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={watch("dedctions_options")}
                  onChange={hanldeDeductionOptions}
                  sx={optionsStyle}
                >
                  {deductionOptions.map((opt) => {
                    const isActive = watch("dedctions_options") === opt.id;
                    return (
                      <Box key={opt.id} sx={cardStyle(isActive)}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Radio value={opt.id} />
                          <Typography
                            fontWeight="medium"
                            sx={{
                              color: isActive
                                ? themes.stepActiveColor
                                : "inherit",
                            }}
                          >
                            {opt.label}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 4, maxWidth: 250, wordBreak: "break-word" }}
                        >
                          {opt.description}
                        </Typography>
                      </Box>
                    );
                  })}
                </RadioGroup>
              </Box>

              {watch("dedctions_options") === "Attendance" ? (
                <>
                  <Box sx={additionalInfoStyle}>
                    <Box sx={individualBoxStyle}>
                      <Typography variant="body2" color="text.secondary">
                        For the first
                      </Typography>
                      <CustomBox
                        name="attendance_first_mins"
                        type="minutes"
                        value={watch("attendance_first_mins")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue("attendance_first_mins", value);
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />

                      <Typography variant="body2" color="text.secondary">
                        mins of late arrival or early exit,a
                      </Typography>
                      <CustomBox
                        name="attendance_late_exit"
                        type="minutes"
                        value={watch("attendance_late_exit")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue("attendance_late_exit", value);
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />

                      <Typography variant="body2" color="text.secondary">
                        mins deduction
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        will be applied
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          width: 180,
                          verticalAlign: "middle",
                        }}
                      >
                        <CustomAutoComplete
                          dataList={type
                            ?.filter((obj) => obj.label !== "Individual OT")
                            .map((obj) => ({
                              ...obj,
                              label: obj.label,
                            }))}
                          rules={{
                            ...register("deductions_attendance_deduct_type", {
                              required: false,
                              validate: {
                                minLengthNoSpaces: () => {
                                  if (
                                    watch("is_apply_sft_penalty_policy") &&
                                    watch("penalty_options") === "hours" &&
                                    watch("dedctions_options") ===
                                      "Attendance" &&
                                    watch(
                                      "deductions_attendance_deduct_type",
                                    ) === ""
                                  ) {
                                    return "Value is required";
                                  }
                                  return true;
                                },
                              },
                            }),
                          }}
                          selectedValue={watch(
                            "deductions_attendance_deduct_type",
                          )}
                          updateDataFunction={(event, data) =>
                            setValue(
                              "deductions_attendance_deduct_type",
                              data?.label,
                            )
                          }
                          placeHolder="Select type"
                          errorMessage={
                            errors?.deductions_attendance_deduct_type?.message
                              ? errors?.deductions_attendance_deduct_type
                                  ?.message
                              : ""
                          }
                          textBoxError={
                            errors?.deductions_attendance_deduct_type
                              ? true
                              : false
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={additionalInfoStyle}>
                    <Box sx={individualBoxStyle}>
                      <Typography variant="body2" color="text.secondary">
                        After that for every additional
                      </Typography>
                      <CustomBox
                        name="attendance_initial_mins"
                        type="minutes"
                        value={watch("attendance_initial_mins")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue("attendance_initial_mins", value);
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />

                      <Typography variant="body2" color="text.secondary">
                        mins of early/late, a
                      </Typography>
                      <CustomBox
                        name="attendance_additional_mins"
                        type="minutes"
                        value={watch("attendance_additional_mins")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue("attendance_additional_mins", value);
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />

                      <Typography variant="body2" color="text.secondary">
                        mins
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        deduction will be applied.
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={infoMessageStyle}>
                    <SftPenaltyPolicyExample />
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={additionalInfoStyle}>
                    <Box sx={individualBoxStyle}>
                      <Typography variant="body2" color="text.secondary">
                        If an employee is late arrival or early exit by
                      </Typography>
                      <CustomBox
                        name="fixed_amount_early_late"
                        type="minutes"
                        value={watch("fixed_amount_early_late")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue("fixed_amount_early_late", value);
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />

                      <Typography variant="body2" color="text.secondary">
                        mins
                      </Typography>
                      <CustomBox
                        name="fixed_amount_deduct_amount"
                        type="amount"
                        value={watch("fixed_amount_deduct_amount")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue("fixed_amount_deduct_amount", value);
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />

                      <Typography variant="body2" color="text.secondary">
                        amount will
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        be deducted
                      </Typography>

                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          width: 180,
                          verticalAlign: "middle",
                        }}
                      >
                        <CustomAutoComplete
                          dataList={type?.map((obj) => ({
                            ...obj,
                            label: obj.label,
                          }))}
                          rules={{
                            ...register("deductions_fixed_amount_deduct_type", {
                              required: false,
                              validate: {
                                minLengthNoSpaces: () => {
                                  if (
                                    watch("is_apply_sft_penalty_policy") &&
                                    watch("penalty_options") === "hours" &&
                                    watch("dedctions_options") !==
                                      "Attendance" &&
                                    watch(
                                      "deductions_fixed_amount_deduct_type",
                                    ) === ""
                                  ) {
                                    return "Value is required";
                                  }
                                  return true;
                                },
                              },
                            }),
                          }}
                          selectedValue={watch(
                            "deductions_fixed_amount_deduct_type",
                          )}
                          updateDataFunction={(event, data) =>
                            setValue(
                              "deductions_fixed_amount_deduct_type",
                              data?.label,
                            )
                          }
                          placeHolder="Select type"
                          errorMessage={
                            errors?.deductions_fixed_amount_deduct_type?.message
                              ? errors?.deductions_fixed_amount_deduct_type
                                  ?.message
                              : ""
                          }
                          textBoxError={
                            errors?.deductions_fixed_amount_deduct_type
                              ? true
                              : false
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={additionalInfoStyle}>
                    <Box sx={individualBoxStyle}>
                      <Typography variant="body2" color="text.secondary">
                        For every additional
                      </Typography>
                      <CustomBox
                        name="fixed_amount_extra_mins"
                        type="minutes"
                        value={watch("fixed_amount_extra_mins")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue("fixed_amount_extra_mins", value);
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />

                      <Typography variant="body2" color="text.secondary">
                        minutes, an extra
                      </Typography>
                      <CustomBox
                        name="fixed_amount_early_late_deduct_amt"
                        type="amount"
                        value={watch("fixed_amount_early_late_deduct_amt")}
                        register={register}
                        errors={errors}
                        handleChange={(e, value) => {
                          setValue(
                            "fixed_amount_early_late_deduct_amt",
                            value,
                          );
                        }}
                        rules={{ required: "Enter value" }}
                        inputWidth="50px"
                      />
                      <Typography variant="body2" color="text.secondary">
                        amount will be deducted.
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={infoMessageStyle}>
                    <SftPenaltyPolicyExample />
                  </Box>
                </>
              )}
            </>
          )}

          {watch("penalty_options") === "shift" && (
            <>
              <Box sx={additionalInfoStyle}>
                <Box sx={individualBoxStyle}>
                  <Typography variant="body2" color="text.secondary">
                    If employee late arrival or early exit by
                  </Typography>
                  <CustomBox
                    name="sft_penalty_early_late_mins"
                    type="minutes"
                    value={watch("sft_penalty_early_late_mins")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_early_late_mins", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    mins then deduction will be
                  </Typography>
                  <CustomBox
                    name="sft_penalty_deduct"
                    type="shift"
                    value={watch("sft_penalty_deduct")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_deduct", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    shift
                  </Typography>
                </Box>
              </Box>
              <Box sx={additionalInfoStyle}>
                <Box sx={individualBoxStyle}>
                  <Typography variant="body2" color="text.secondary">
                    For every additional
                  </Typography>
                  <CustomBox
                    name="sft_penalty_additional_mins"
                    type="minutes"
                    value={watch("sft_penalty_additional_mins")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_additional_mins", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    mins the deduction will be
                  </Typography>
                  <CustomBox
                    name="sft_penalty_sft_deduct"
                    type="shift"
                    value={watch("sft_penalty_sft_deduct")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_sft_deduct", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    shift
                  </Typography>
                </Box>
              </Box>
              <Box sx={infoMessageStyle}>
                <SftPenaltyPolicySftExample />
              </Box>
            </>
          )}

          {watch("penalty_options") === "count" && (
            <>
              <Box sx={additionalInfoStyle}>
                <Box sx={individualBoxStyle}>
                  <Typography variant="body2" color="text.secondary">
                    If employee late arrival or early exit more than
                  </Typography>
                  <CustomBox
                    name="sft_penalty_count_thershold"
                    type="minutes"
                    value={watch("sft_penalty_count_thershold")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_count_thershold", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    times then deduction will be
                  </Typography>
                  <CustomBox
                    name="sft_penalty_count_deduct"
                    type="shift"
                    value={watch("sft_penalty_count_deduct")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_count_deduct", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    shift
                  </Typography>
                </Box>
              </Box>
              <Box sx={additionalInfoStyle}>
                <Box sx={individualBoxStyle}>
                  <Typography variant="body2" color="text.secondary">
                    For every additional
                  </Typography>
                  <CustomBox
                    name="sft_penalty_count_additional_mins"
                    type="minutes"
                    value={watch("sft_penalty_count_additional_mins")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_count_additional_mins", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    the deduction will be
                  </Typography>
                  <CustomBox
                    name="sft_penalty_count_sft_deduct"
                    type="shift"
                    value={watch("sft_penalty_count_sft_deduct")}
                    register={register}
                    errors={errors}
                    handleChange={(e, value) => {
                      setValue("sft_penalty_count_sft_deduct", value);
                    }}
                    rules={{ required: "Enter value" }}
                    inputWidth="50px"
                  />
                  <Typography variant="body2" color="text.secondary">
                    shift
                  </Typography>
                </Box>
              </Box>
              <Box sx={infoMessageStyle}>
                <SftPenaltyPolicyCountExample />
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};
